import React, { useState, useEffect } from 'react';
import {
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
  Table,
  Spinner,
  Badge,
  Input,
  FormGroup,
  Label,
  Button,
} from 'reactstrap';
import moment from 'moment';
import { fetchPurifierServiceHistory } from '../actions/action';
import { useDispatch } from 'react-redux';

const PurifierServiceHistoryOffcanvas = ({ isOpen, toggle, lsOrderNo }) => {
  const [loading, setLoading] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const dispatch = useDispatch();

  const handleCreateServiceOrder = () => {
    if (!orderDetails) return;
    const prefill = {
      customer_name: orderDetails.customer_name || '',
      email: orderDetails.email || '',
      mobile: (orderDetails.mobile || '').replace(/^971/, ''),
      Mobile_Number: orderDetails.mobile || '',
      model: orderDetails.model_no || '',
      product_name: orderDetails.model_no || '',
      item_code: orderDetails.sku || '',
      sku: orderDetails.sku || '',
      serial_no: orderDetails.serial_no || '',
      so_no: orderDetails.ls_order_no || '',
      purchase_date: orderDetails.installation_date || '',
      warranty_end_date: orderDetails.warranty_end_date || '',
      amc_start_date: orderDetails.amc_start_date || '',
      amc_end_date: orderDetails.amc_end_date || '',
      is_amc:
        !!orderDetails.amc_end_date &&
        new Date(orderDetails.amc_end_date) >= new Date(),
      // address subfields (form reads capitalized variants)
      buildingName: orderDetails.building || '',
      streetName: orderDetails.street || '',
      flatNo: orderDetails.flat || '',
      floorNumber: orderDetails.floor || '',
      areaName: orderDetails.area || '',
      Area: orderDetails.area || '',
      Emirate: orderDetails.emirate || '',
      nearestLandMark: orderDetails.land_mark || '',
      Latitude: orderDetails.latitude || '',
      Longitude: orderDetails.longitude || '',
      // fallback long-form address strings
      Address: orderDetails.address1 || '',
      Address_2: orderDetails.address2 || '',
      Budget_Category: 'PURIFIER',
      Sub_Group: 'Water Purifier',
    };
    try {
      localStorage.setItem('__prefillCreateServiceOrder', JSON.stringify(prefill));
    } catch (e) {
      // localStorage may be unavailable; we still attempt to open.
    }
    window.open('/service-orders/manage-service-orders?openCreate=1', '_blank');
  };

  useEffect(() => {
    if (isOpen && lsOrderNo) {
      setSearchTerm('');
      fetchHistory();
    }
  }, [isOpen, lsOrderNo]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const data = await fetchPurifierServiceHistory({
        params: { ls_order_no: lsOrderNo },
        dispatch,
      });
      // The API returns the data directly as the payload or wrapped.
      // Based on common patterns in this project, it's likely data.data or just data.
      setHistoryData(data?.data || data || null);
    } catch (error) {
      console.error('Error fetching service history:', error);
    } finally {
      setLoading(false);
    }
  };

  const serviceHistory = historyData?.serviceList || [];
  const orderDetails = historyData?.order || null;

  const filteredHistory = serviceHistory.filter((item) => {
    const search = searchTerm.toLowerCase();
    return (
      item.complaint_no?.toLowerCase().includes(search) ||
      item.service_order_no?.toLowerCase().includes(search)
    );
  });

  return (
    <Offcanvas
      isOpen={isOpen}
      toggle={toggle}
      direction="end"
      style={{ width: '50vw', maxWidth: '900px' }}
      className="border-start-0 shadow"
    >
      <OffcanvasHeader toggle={toggle} className="border-bottom bg-light py-3">
        <div className="d-flex align-items-center">
          <i className="mdi mdi-history text-warning me-2 fs-4"></i>
          <span className="fw-bold">Service History</span>
          <span className="mx-2 text-muted fw-normal">|</span>
          <span className="text-secondary fw-bold">{lsOrderNo}</span>
        </div>
      </OffcanvasHeader>
      <OffcanvasBody className="p-4">
        {loading ? (
          <div className="d-flex justify-content-center py-5">
            <Spinner color="warning" />
          </div>
        ) : serviceHistory.length > 0 ? (
          <>
            <FormGroup className="mb-4">
              <div className="position-relative">
                <Input
                  type="text"
                  placeholder="Search by SR No or Service Order No..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-control ps-4"
                  style={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                />
                <i
                  className="mdi mdi-magnify position-absolute top-50 start-0 translate-middle-y ms-2 text-muted"
                  style={{ left: '10px' }}
                ></i>
              </div>
            </FormGroup>

            <div className="table-responsive rounded-3 border">
              <Table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th
                      className="py-3 ps-3"
                      style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}
                    >
                      DATE
                    </th>
                    <th
                      className="py-3"
                      style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}
                    >
                      SR NO / SERVICE NO
                    </th>
                    <th
                      className="py-3"
                      style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}
                    >
                      TYPE
                    </th>
                    <th
                      className="py-3"
                      style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}
                    >
                      AMOUNT
                    </th>
                    <th
                      className="py-3 pe-3"
                      style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}
                    >
                      STATUS
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredHistory.length > 0 ? (
                    filteredHistory.map((item, index) => {
                      const status = item?.statusInfo?.status_label?.toLowerCase() || '';
                                  let badgeColor =
                                    'bg-soft-light text-dark border-light';
                                  let iconClass = 'mdi mdi-information-outline';

                                  if (status === 'new') {
                                    badgeColor = 'bg-soft-info text-info';
                                    iconClass = 'mdi mdi-star-circle-outline';
                                  } else if (status === 'canceled') {
                                    badgeColor = 'bg-soft-danger text-danger';
                                    iconClass = 'mdi mdi-close-circle-outline';
                                  } else if (
                                    status === 'assigned to technician' ||
                                    status === 'in progress'
                                  ) {
                                    badgeColor = 'bg-soft-info text-info';
                                    iconClass = 'mdi mdi-progress-clock';
                                  } else if (status === 'resolved') {
                                    badgeColor = 'bg-soft-success text-success';
                                    iconClass = 'mdi mdi-check-circle-outline';
                                  } else if (status === 'rescheduled') {
                                    badgeColor = 'bg-soft-warning text-warning';
                                    iconClass = 'mdi mdi-calendar-sync';
                                  }
                      
                                  return (
                      <tr key={index}>
                        <td className="ps-3">
                          <div className="fw-medium text-dark">
                            {moment(new Date(item.created_at)).format('DD MMM YYYY')}
                          </div>
                          <small className="text-muted">
                            {moment(new Date(item.created_at)).format('h:mm A')}
                          </small>
                        </td>
                        <td>
                          <div
                            className="fw-bold text-primary"
                            style={{ cursor: 'pointer', textDecoration: 'underline' }}
                            onClick={() => {
                              if (item.complaint_no) {
                                window.open(
                                  `/service-orders/manage-service-orders/${item.complaint_no}`,
                                  '_blank',
                                );
                              }
                            }}
                          >
                            {item.complaint_no}
                          </div>
                          {item.service_order_no && (
                            <small className="text-muted d-block">
                              SO: {item.service_order_no}
                            </small>
                          )}
                        </td>
                        <td>
                          <div className="text-capitalize small fw-semibold text-muted">
                            {item.service_type || '-'}
                          </div>
                        </td>
                        <td>
                          <div className="small text-muted" style={{ whiteSpace: 'nowrap' }}>
                            <div className="d-flex gap-3">
                              <div className="text-center flex-fill">Sub Total</div>
                              <div className="text-center flex-fill">Discount</div>
                              <div className="text-center flex-fill">Order Total</div>
                            </div>
                            <div className="d-flex gap-3">
                              <b className="text-dark text-center flex-fill">{Number(item?.sub_total ?? 0).toFixed(2)}</b>
                              <b className="text-dark text-center flex-fill">{Number(item?.discount_total ?? 0).toFixed(2)}</b>
                              <b className="text-dark text-center flex-fill">{Number(item?.order_total ?? 0).toFixed(2)}</b>
                            </div>
                            {(() => {
                              let info = item?.payment_info;
                              if (typeof info === 'string') {
                                try {
                                  info = JSON.parse(info);
                                } catch (e) {
                                  info = null;
                                }
                              }
                              if (!info) return null;
                              const provider = info.provider;
                              const providerId = info.paymentProviderId;
                              if (!provider && !providerId) return null;
                              return (
                                <div className="mt-1 pt-1 border-top text-center">
                                  <b className="text-dark">
                                    {provider}
                                    {provider && providerId && (
                                      <span className="mx-2 text-muted fw-normal">|</span>
                                    )}
                                    {providerId}
                                  </b>
                                </div>
                              );
                            })()}
                          </div>
                        </td>
                        <td className="pe-3">
                          <div
                                        style={{
                                          borderColor: 'var(--bs-border-color)',
                                        }}
                                        className={`badge fs-12 px-2 py-1 text-capitalize ${badgeColor} border d-inline-flex align-items-center gap-1`}
                                      >
                                        <i className={`${iconClass} fs-14`}></i>
                                        {item.statusInfo?.status_label}
                                      </div>
                        </td>
                      </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center py-4 text-muted">
                        No results matching your search terms.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </>
        ) : (
          <div className="text-center py-5">
            <i className="mdi mdi-database-off-outline d-block fs-1 mb-2 opacity-25"></i>
            <p className="text-muted">No service history found for this order.</p>
          </div>
        )}

        {orderDetails && (
          <div className="mt-4 p-4 bg-light rounded-3 border bg-opacity-50">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div className="d-flex align-items-center">
                <i className="mdi mdi-information-outline text-info me-2 fs-5"></i>
                <h6 className="fw-bold mb-0">Complete Order Details</h6>
              </div>
              <Button
                color="dark"
                size="sm"
                onClick={handleCreateServiceOrder}
              >
                <i className="mdi mdi-plus-circle-outline me-1"></i>
                Create Service Order
              </Button>
            </div>
            
            <div className="row g-4">
              {/* Customer Info */}
              <div className="col-12">
                <div className="p-3 bg-white rounded-3 shadow-sm border-start border-4 border-info">
                  <div className="row">
                    <div className="col-md-6 mb-3 mb-md-0">
                      <label className="text-muted small text-uppercase fw-bold d-block mb-1">Customer Name</label>
                      <span className="fw-semibold text-dark fs-14">{orderDetails.customer_name}</span>
                    </div>
                    <div className="col-md-6">
                      <label className="text-muted small text-uppercase fw-bold d-block mb-1">Contact Details</label>
                      <div className="d-flex flex-column">
                        <span className="fw-semibold text-dark fs-14">
                          <i className="mdi mdi-phone-outline me-1"></i>{orderDetails.mobile}
                        </span>
                        {orderDetails.email && (
                          <span className="text-muted small">
                            <i className="mdi mdi-email-outline me-1"></i>{orderDetails.email}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Product Info */}
              <div className="col-md-6">
                <label className="text-muted small text-uppercase fw-bold d-block mb-1">Product Info</label>
                <div className="d-flex flex-column gap-2">
                  <div>
                    <small className="text-muted d-block">SKU / Model</small>
                    <span className="fw-medium">{orderDetails.sku}</span>
                    <span className="mx-1 text-muted">|</span>
                    <span className="fw-medium text-primary">{orderDetails.model_no}</span>
                  </div>
                  <div>
                    <small className="text-muted d-block">LS Order No</small>
                    <span className="fw-bold text-dark">{orderDetails.ls_order_no}</span>
                  </div>
                </div>
              </div>

              {/* Location Info */}
              <div className="col-md-6">
                <label className="text-muted small text-uppercase fw-bold d-block mb-1">Location</label>
                <div className="d-flex flex-column gap-1">
                  <span className="fw-medium text-dark">{orderDetails.address1}</span>
                  <div className="d-flex align-items-center gap-2">
                    <Badge color="soft-dark" className="fw-normal">{orderDetails.area}</Badge>
                    <Badge color="soft-primary" className="fw-normal">{orderDetails.emirate}</Badge>
                  </div>
                </div>
              </div>

              {/* Dates & Warranty */}
              <div className="col-12">
                <hr className="my-2 border-dashed" />
                <div className="row mt-3">
                  <div className="col-md-4 mb-3 mb-md-0">
                    <label className="text-muted small text-uppercase fw-bold d-block mb-1">Installation</label>
                    <div className="d-flex align-items-center text-success">
                      <i className="mdi mdi-calendar-check me-1 fs-5"></i>
                      <span className="fw-medium">
                        {orderDetails.installation_date ? moment(new Date(orderDetails.installation_date)).format('DD MMM YYYY') : '-'}
                      </span>
                    </div>
                  </div>
                  <div className="col-md-4 mb-3 mb-md-0">
                    <label className="text-muted small text-uppercase fw-bold d-block mb-1">Warranty Period</label>
                    <div className="d-flex align-items-center text-primary">
                      <i className="mdi mdi-shield-badge-outline me-1 fs-5"></i>
                      <span className="fw-medium">
                        Until {orderDetails.warranty_end_date ? moment(new Date(orderDetails.warranty_end_date)).format('DD MMM YYYY') : '-'}
                      </span>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <label className="text-muted small text-uppercase fw-bold d-block mb-1">Order Created</label>
                    <div className="text-muted small">
                      {orderDetails.created_at ? moment(new Date(orderDetails.created_at)).format('DD MMM YYYY, h:mm A') : '-'}
                    </div>
                  </div>
                </div>
              </div>

              {/* AMC Info */}
              {(orderDetails.amc_start_date || orderDetails.amc_end_date) && (
                <div className="col-12">
                  <div className="p-2 px-3 bg-soft-warning border border-warning border-opacity-25 rounded-3">
                    <div className="d-flex align-items-center gap-2">
                      <i className="mdi mdi-certificate text-warning fs-4"></i>
                      <div>
                        <small className="text-muted small text-uppercase fw-bold d-block" style={{ fontSize: '10px' }}>AMC Contract Period</small>
                        <span className="fw-bold text-dark">
                          {moment(new Date(orderDetails.amc_start_date)).format('DD MMM YYYY')} 
                          <span className="mx-2 text-muted fw-normal">to</span>
                          {moment(new Date(orderDetails.amc_end_date)).format('DD MMM YYYY')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </OffcanvasBody>
    </Offcanvas>
  );
};

export default PurifierServiceHistoryOffcanvas;
